import { HttpResponse } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  debounce,
  Subject,
  switchMap,
  takeUntil,
  throwError,
  timer
} from 'rxjs';
import { HeaderDto } from 'src/app/dto/header.dto';
import { AuthService } from 'src/app/services/auth-service.service';
import {
  CheckInnDTO,
  RegistrationRequestData,
  RegistrationService
} from 'src/app/services/registration.service';
import { catchError, take } from 'rxjs/operators';
import { TaxNumberValidatorService } from '../../services/tax-number-validator.service';

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss']
})
export class RegistrationComponent implements OnDestroy {
  @ViewChild('scrollToElement') scrollToElement: ElementRef;

  readonly regForm: FormGroup;

  digits = [0, 1, 2, 3, 4, 5];
  otp: string[] = ['', '', '', '', '', ''];

  headerDataValues: HeaderDto = {
    title: 'Регистрация'
  };

  checkInnData?: CheckInnDTO;

  showCodeForm: boolean = false;

  submitCodeErrorFlag: boolean = false;

  registrationErrorText: string = '';

  isToastOpen = false;

  private PasswordSymbolsPattern: RegExp = /^\S*$/;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private registrationService: RegistrationService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    taxNumberValidatorService: TaxNumberValidatorService
  ) {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(parameter => {
        if (parameter['email']) {
          this.showCodeForm = true;
          this.regForm.get('email')?.setValue(parameter['email']);
          this.regForm.get('email')?.updateValueAndValidity();
        }
      });

    this.regForm = new FormGroup({
      inn: new FormControl('', [
        Validators.required,
        Validators.minLength(12),
        taxNumberValidatorService.createValidator()
      ]),
      lastName: new FormControl('', [Validators.required]),
      firstName: new FormControl('', [Validators.required]),
      patronymicName: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required]),
      phone: new FormControl('', [Validators.required]),
      password: new FormControl('', [
        Validators.required,
        Validators.pattern(this.PasswordSymbolsPattern)
      ]),
      passwordRepeat: new FormControl('', [
        Validators.required,
        Validators.pattern(this.PasswordSymbolsPattern)
      ]),
      userDataAgreement: new FormControl(false, [Validators.required]),
      licenseAgreement: new FormControl(false, [Validators.required])
    });

    const innControl = this.regForm.get('inn');
    const emailControl = this.regForm.get('email');
    const firstNameControl = this.regForm.get('firstName');
    const lastNameControl = this.regForm.get('lastName');
    const patronymicNameControl = this.regForm.get('patronymicName');
    const phoneControl = this.regForm.get('phone');
    const passwordControl = this.regForm.get('password');
    const passwordRepeatControl = this.regForm.get('passwordRepeat');

    innControl?.statusChanges
      .pipe(
        debounce(() => timer(200)),
        takeUntil(this.destroy$)
      )
      .subscribe((status: any) => {
        const typedInn = innControl.value;

        if (status === 'VALID' && typedInn.length === 12) {
          this.registrationService
            .checkInn(typedInn, 'SELF_EMPLOYED')
            .pipe(take(1))
            .subscribe({
              next: v => {
                this.checkInnData = v;

                if (
                  emailControl &&
                  !emailControl.value &&
                  this.checkInnData.data.email
                ) {
                  emailControl?.setValue(this.checkInnData.data.email);
                  emailControl.updateValueAndValidity();
                }

                // ФИО безусловно отражают ТЕКУЩИЙ ИНН (поля только для чтения — иного
                // источника нет). При смене ИНН значения перезаписываются, отсутствующее
                // отчество очищается ('') — иначе осталось бы ФИО от прежнего ИНН.
                lastNameControl?.setValue(this.checkInnData.data.lastName || '');
                lastNameControl?.updateValueAndValidity();
                firstNameControl?.setValue(this.checkInnData.data.firstName || '');
                firstNameControl?.updateValueAndValidity();
                patronymicNameControl?.setValue(
                  this.checkInnData.data.patronymicName || ''
                );
                patronymicNameControl?.updateValueAndValidity();

                if (
                  phoneControl &&
                  !phoneControl.value &&
                  this.checkInnData.data.phone
                ) {
                  phoneControl?.setValue(this.checkInnData.data.phone);
                  phoneControl.updateValueAndValidity();
                }
              },
              error: e => {
                // ИНН отклонён (напр. закрытый ИП): сбрасываем данные и ФИО, чтобы не
                // осталось значений от прежнего валидного ИНН и отправка была невозможна.
                this.checkInnData = undefined;
                this.resetFullNameFields();
                // markAsTouched строго до setErrors: base-input пересчитывает isInvalid
                // (= invalid && touched) по statusChanges, который эмитит setErrors. Иначе
                // при незаблюренном поле сообщение (напр. о закрытом ИП) не показалось бы сразу.
                innControl?.markAsTouched();
                innControl?.setErrors({ incorrect: e.error_description });
              }
            });
        } else this.clearCheckInnField();
      });

    passwordControl!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkPasswordMatch();
      });

    passwordRepeatControl!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkPasswordMatch();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkPasswordMatch(): void {
    const password = this.regForm.get('password')?.value;
    const passwordRepeat = this.regForm.get('passwordRepeat')?.value;

    const regex = /^\S*$/; // Регулярное выражение
    let regexFlag = true;

    if (
      !password ||
      !passwordRepeat ||
      !regex.test(password) ||
      !regex.test(passwordRepeat)
    ) {
      regexFlag = false;
    }

    if (password === passwordRepeat && regexFlag) {
      this.regForm.get('passwordRepeat')!.setErrors(null);
    }
    if (password !== passwordRepeat && regexFlag) {
      this.regForm.get('passwordRepeat')!.setErrors({ mismatch: true });
    }
  }

  acceptUserDataAgreement(): void {
    const { userDataAgreement } = this.regForm.controls;
    userDataAgreement.setValue(!userDataAgreement.value);
  }

  acceptLicenseAgreement(): void {
    const { licenseAgreement } = this.regForm.controls;
    licenseAgreement.setValue(!licenseAgreement.value);
  }

  registration() {
    let phone = '';
    const inn = this.regForm.get('inn')?.value;
    const innCheckInfo = this.checkInnData;
    if (!innCheckInfo) {
      return;
    }
    const email = this.regForm.get('email')?.value;

    phone = `+${this.regForm.get('phone')?.value.replace(/\D/g, '')}`;

    const password = this.regForm.get('password')?.value;
    const requestData: RegistrationRequestData = {
      userType: 'SELF_EMPLOYED',
      inn,
      email,
      phone,
      password,
      firstName: innCheckInfo.data.firstName,
      lastName: innCheckInfo.data.lastName,
      patronymicName: innCheckInfo.data.patronymicName,
      name: innCheckInfo.data.name
    };

    this.registrationService
      .registration(requestData)
      .pipe(take(1))
      .subscribe({
        next: (data: HttpResponse<void>) => {
          this.registrationErrorText = '';
          if (data.status === 200) {
            this.showCodeForm = true;
          }
        },
        error: error => {
          if (error.status === 409) {
            this.scrollTo(error.error.conflictReason);
            this.regForm.get(error.error.conflictReason)?.setValue('');
            this.regForm
              .get(error.error.conflictReason)
              ?.setErrors({ alreadyExist: true });
            this.regForm.get('password')?.reset();
            this.regForm.get('passwordRepeat')?.reset();
          }

          if (error.status === 418) {
            this.showCodeForm = true;
          }

          this.registrationErrorText = error.error.error_description;

          this.regForm.get('password')?.reset();
          this.regForm.get('passwordRepeat')?.reset();
        }
      });
  }

  clearCheckInnField(): void {
    this.checkInnData = undefined;
    // ИНН неполный/невалидный — ФИО (только для чтения) не должны показывать данные
    // от прежде введённого ИНН.
    this.resetFullNameFields();
  }

  focusNextInput(index: number): void {
    // Фокусируем на следующем поле при вводе
    if (index < this.digits.length - 1 && this.otp[index]) {
      document.getElementById(`otp${index + 1}`)?.focus();
    }
    if (index > 0 && !this.otp[index]) {
      if (this.submitCodeErrorFlag) this.submitCodeErrorFlag = false;

      document.getElementById(`otp${index - 1}`)?.focus();
    }

    if (index === this.digits.length - 1 && this.otp[index]) {
      this.sendCode();
    }
  }

  sendCode(): void {
    this.registrationService
      .sendCode(this.regForm.get('email')!.value!, this.otp.join(''))
      .pipe(
        take(1),
        catchError(err => {
          this.submitCodeErrorFlag = true;
          return throwError(() => err);
        }),
        switchMap(data =>
          this.authService.registrationEnd(
            data.data.access_token,
            data.data.refresh_token,
            data.data.refresh_expires_in
          )
        )
      )
      .subscribe(() => this.resetForm());
  }

  resendCode(): void {
    this.registrationService
      .resendCode(this.regForm.get('email')!.value!)
      .pipe(take(1))
      .subscribe(() => {
        this.toastSetStatus(true);
      });
  }

  openLogin(): void {
    this.router.navigate(['auth']);
  }

  scrollTo(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  toastSetStatus(isOpen: boolean): void {
    this.isToastOpen = isOpen;
  }

  openUserAgreement(): void {
    this.router.navigate(['user-agreement']);
  }

  openDataProcessingTerms(): void {
    this.router.navigate(['data-processing-terms']);
  }

  /** Очищает автозаполняемые поля ФИО (Фамилия/Имя/Отчество). */
  private resetFullNameFields(): void {
    this.regForm.get('lastName')?.setValue('');
    this.regForm.get('firstName')?.setValue('');
    this.regForm.get('patronymicName')?.setValue('');
  }

  private resetForm(): void {
    this.regForm.reset();
    this.otp = ['', '', '', '', '', ''];
    this.registrationErrorText = '';
  }
}
